import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/auth';
import { supabase } from '@/lib/supabase/client';

type CallbackStatus = 'processing' | 'success' | 'error';

export default function AuthCallbackPage() {
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, initializeAuth } = useAuthStore();

  const hasProcessedRef = useRef(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRedirect = (path: string, delayMs = 500) => {
    if (redirectTimerRef.current) return;
    redirectTimerRef.current = setTimeout(() => {
      navigate(path, { replace: true });
    }, delayMs);
  };

  useEffect(() => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const handleCallback = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const authType = searchParams.get('type') || hashParams.get('type');
        const code = searchParams.get('code');

        if (authType === 'recovery') {
          scheduleRedirect(`/auth/update-password${window.location.hash || ''}`);
          return;
        }

        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            setErrorMessage(exchangeError.message);
            setStatus('error');
            return;
          }
        }

        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        if (errorParam) {
          // Map OAuth error codes to user-friendly messages
          const errorMessages: Record<string, string> = {
            'access_denied': 'Sign-in was cancelled. Please try again.',
            'invalid_request': 'The sign-in request was invalid. Please try again.',
            'unauthorized_client': 'This application is not authorized to complete sign-in.',
            'unsupported_response_type': 'The authentication method is not supported.',
            'invalid_scope': 'The requested permissions are not valid.',
            'server_error': 'There was a problem with the authentication service. Please try again later.',
            'temporarily_unavailable': 'The authentication service is temporarily unavailable. Please try again later.',
            'expired_state': 'Your sign-in session has expired. Please try again.',
            'invalid_state': 'There was a problem with your sign-in session. Please try again.'
          };
          
          const friendlyMessage = errorMessages[errorParam] || errorDescription || errorParam;
          setErrorMessage(friendlyMessage);
          setStatus('error');
          return;
        }

        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          setErrorMessage(error.message);
          setStatus('error');
          return;
        }

        if (session) {
          setStatus('success');
          await initializeAuth();
          
          // Check for pending redirect after successful OAuth signin
          const pendingRedirect = sessionStorage.getItem('postLoginRedirect');
          const redirectTo = pendingRedirect || '/dashboard';
          if (pendingRedirect) {
            sessionStorage.removeItem('postLoginRedirect');
          }
          scheduleRedirect(redirectTo, 800);
          return;
        }

        // Brief bounded polling to wait for session establishment without extra subscriptions.
        const pendingRedirect = sessionStorage.getItem('postLoginRedirect');
        const redirectTo = pendingRedirect || '/dashboard';
        
        for (let attempt = 0; attempt < 15; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          const { data: { session: retrySession } } = await supabase.auth.getSession();
          if (retrySession) {
            setStatus('success');
            await initializeAuth();
            if (pendingRedirect) {
              sessionStorage.removeItem('postLoginRedirect');
            }
            scheduleRedirect(redirectTo, 500);
            return;
          }
        }

        setErrorMessage('Authentication timed out - please try again');
        setStatus('error');
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred');
        setStatus('error');
      }
    };

    void handleCallback();

    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, [initializeAuth, navigate, searchParams]);

  useEffect(() => {
    if (isAuthenticated && status === 'processing') {
      setStatus('success');
      const pendingRedirect = sessionStorage.getItem('postLoginRedirect');
      const redirectTo = pendingRedirect || '/dashboard';
      if (pendingRedirect) {
        sessionStorage.removeItem('postLoginRedirect');
      }
      scheduleRedirect(redirectTo, 300);
    }
  }, [isAuthenticated, status]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Authentication Failed</h3>
                <p className="text-sm text-muted-foreground">
                  {errorMessage || 'We couldn\'t complete your sign-in. Please try again.'}
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/signin', { replace: true })}>
                  Back to Sign In
                </Button>
                <Button onClick={() => {
                  hasProcessedRef.current = false;
                  setStatus('processing');
                  setErrorMessage(null);
                  window.location.reload();
                }}>
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
              <div className="relative flex h-14 w-14 items-center justify-center">
                <CheckCircle className="h-12 w-12 text-success" />
                <Loader2 className="absolute -right-1 -bottom-1 h-5 w-5 animate-spin text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Sign-in successful!</h3>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to your dashboard...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Completing sign-in...</h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we verify your account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
